// import { Language } from '@/services/interfaces/TranslationService';

export interface MicrophoneDevice {
  deviceId: string;
  label: string;
  groupId: string;
}

export interface MicrophoneServiceConfig {
  onAudioData?: (audioData: Float32Array) => void;
  onPermissionChange?: (hasPermission: boolean) => void;
  onDevicesChange?: (devices: MicrophoneDevice[]) => void;
  onAudioLevel?: (level: number) => void;
}

export class MicrophoneService {
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private processorNode: AudioWorkletNode | null = null;
  private isInitialized: boolean = false;
  _config: MicrophoneServiceConfig = {};
  private permissionStatus: 'prompt' | 'granted' | 'denied' | 'unknown' = 'unknown';
  private availableDevices: MicrophoneDevice[] = [];
  private selectedDeviceId: string = '';

  // Add instance tracking for memory debugging
  private static instanceCount = 0;
  private instanceId: number;

  constructor(config?: MicrophoneServiceConfig) {
    MicrophoneService.instanceCount++;
    this.instanceId = MicrophoneService.instanceCount;
    if (config) {
      this._config = config;
    }
    
    // 监听设备变化
    this.setupDeviceChangeListener();
  }

  // Getter and setter for config with tracking
  // get config(): MicrophoneServiceConfig {
  //   return this._config;
  // }


  /**
   * Detect if we're running on iOS
   */
  private isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           // @ts-ignore - platform is deprecated but still needed for iOS detection
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  /**
   * Request microphone permission and enumerate devices
   */
  async requestPermission(): Promise<boolean> {
    try {
      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('[🎤 MIC] MediaDevices API is not available (likely due to non-HTTPS or unsupported browser)');
        this.permissionStatus = 'denied';
        this._config.onPermissionChange?.(false);
        return false;
      }

      // On iOS, we need to be more aggressive about permission checking
      if (this.isIOS()) {
        console.log('[🎤 MIC] iOS detected - using iOS-optimized permission flow');

        // First check if we already have permission using Permissions API
        const currentStatus = await this.checkPermissionStatus();
        if (currentStatus === 'granted') {
          console.log('[🎤 MIC] Permission already granted on iOS');
          return true;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Stop all tracks immediately - we just wanted permission
      stream.getTracks().forEach(track => track.stop());

      console.log('[🎤 MIC] requestPermission granted');
      this.permissionStatus = 'granted';
      this._config.onPermissionChange?.(true);

      // Re-enumerate devices to get their labels
      await this.enumerateDevices();

      return true;
    } catch (error) {
      console.error('[🎤 MIC] Error requesting microphone permission:', error);
      this.permissionStatus = 'denied';
      this._config.onPermissionChange?.(false);
      return false;
    }
  }

  /**
   * Enumerate available audio input devices
   */
  async enumerateDevices(): Promise<MicrophoneDevice[]> {
    try {
      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.warn('[🎤 MIC] navigator.mediaDevices.enumerateDevices is not available');
        this.permissionStatus = 'denied';
        this._config.onPermissionChange?.(false);
        return [];
      }

      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = deviceList
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`,
          groupId: device.groupId
        }));
      
      this.availableDevices = audioInputDevices;

      // Check permission status based on device labels
      const hasLabels = audioInputDevices.some(device => device.label && !device.label.includes('Microphone'));
      this.permissionStatus = hasLabels ? 'granted' : 'prompt';
      this._config.onPermissionChange?.(hasLabels);

      // Select default device if none is selected and devices are available
      if (audioInputDevices.length > 0 && !this.selectedDeviceId) {
        const defaultDevice = audioInputDevices.find(d => d.deviceId === 'default' || d.deviceId === '') || audioInputDevices[0];
        this.selectedDeviceId = defaultDevice.deviceId;
      }

      // Notify listeners about device changes
      this._config.onDevicesChange?.(audioInputDevices);
      
      return audioInputDevices;
    } catch (error) {
      console.error('[🎤 MIC] Error enumerating devices:', error);
      return [];
    }
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): 'prompt' | 'granted' | 'denied' | 'unknown' {
    return this.permissionStatus;
  }

  /**
   * Check permission status using modern Permissions API with fallback
   */
  async checkPermissionStatus(): Promise<'prompt' | 'granted' | 'denied' | 'unknown'> {
    try {
      // Try modern Permissions API first (supported in most modern browsers)
      if ('permissions' in navigator && 'query' in navigator.permissions) {
        try {
          const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          console.log('[🎤 MIC] Permissions API result:', result.state);

          // Update internal state
          this.permissionStatus = result.state as 'prompt' | 'granted' | 'denied';
          this._config.onPermissionChange?.(result.state === 'granted');

          return this.permissionStatus;
        } catch (permError) {
          console.warn('[🎤 MIC] Permissions API failed:', permError);
        }
      }

      // Fallback: Try to enumerate devices to check permission
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(device => device.kind === 'audioinput');

        // If we have devices with real labels, permission is likely granted
        const hasLabels = audioInputs.some(device =>
          device.label &&
          device.label !== '' &&
          !device.label.includes('Microphone') &&
          !device.label.startsWith('audioinput')
        );

        if (hasLabels) {
          this.permissionStatus = 'granted';
          this._config.onPermissionChange?.(true);
          return 'granted';
        }

        // If we have devices but no labels, permission is likely prompt
        if (audioInputs.length > 0) {
          this.permissionStatus = 'prompt';
          this._config.onPermissionChange?.(false);
          return 'prompt';
        }
      }

      // If no devices found or API not available
      this.permissionStatus = 'unknown';
      this._config.onPermissionChange?.(false);
      return 'unknown';

    } catch (error) {
      console.error('[🎤 MIC] Error checking permission status:', error);
      this.permissionStatus = 'unknown';
      this._config.onPermissionChange?.(false);
      return 'unknown';
    }
  }

  getIsPermissionOK(): boolean {
    return this.permissionStatus === 'granted';
  }

  /**
   * Get available devices
   */
  getAvailableDevices(): MicrophoneDevice[] {
    return [...this.availableDevices];
  }

  /**
   * Get selected device ID
   */
  getSelectedDeviceId(): string {
    return this.selectedDeviceId;
  }

  /**
   * Set selected device ID
   */
  setSelectedDeviceId(deviceId: string): void {
    this.selectedDeviceId = deviceId;
  }

  /**
   * Initialize microphone access with optional device ID
   */
  async initializeMicrophone(microphoneDeviceId?: string): Promise<MediaStream> {
        
    try {
      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('MediaDevices API is not available (likely due to non-HTTPS or unsupported browser)');
      }

      // Close any existing resources
      await this.closeMicrophone();
      
      // Use provided device ID or fall back to selected device
      const deviceId = microphoneDeviceId || this.selectedDeviceId;
      
      // Request microphone access
      const constraints: MediaStreamConstraints = {
        audio: deviceId 
          ? { deviceId: { exact: deviceId } } 
          : true,
        video: false
      };

      const now = Date.now();
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log(`[🎤 MIC] ✅ [MicrophoneService #${this.instanceId}] 麦克风初始化成功，耗时:`, Date.now() - now, 'ms');
      this.isInitialized = true;
        
      return this.mediaStream;
    } catch (error) {
      this.isInitialized = false;
      throw error;
    }
  }

  /**
   * Close and clean up microphone resources
   */
  async closeMicrophone(): Promise<void> {
    // First, mark as not initialized to stop processing
    this.isInitialized = false;
    
    // 暂停音高监听
    await this.endListening();

    // Stop all tracks in the media stream last
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {
        track.stop();
      });
      this.mediaStream = null;
    }
  }

  /**
   * Get the media stream for use in WebRTC
   */
  getMediaStream(): MediaStream | null {
    return this.mediaStream;
  }

  /**
   * Check if microphone is currently initialized
   */
  isActive(): boolean {
    return this.isInitialized;
  }

  /**
   * Get available microphone devices (legacy method for compatibility)
   */
  async getAvailableMicrophones(): Promise<MicrophoneDevice[]> {
    return this.enumerateDevices();
  }

  /**
   * Set up device change listener
   */
  private setupDeviceChangeListener(): void {
    // Check if navigator.mediaDevices is available
    if (!navigator.mediaDevices) {
      console.warn('[🎤 MIC] navigator.mediaDevices is not available (likely due to non-HTTPS or unsupported browser)');
      return;
    }

    try {
      navigator.mediaDevices.addEventListener('devicechange', () => {
        this.enumerateDevices();
      });
    } catch (error) {
      console.warn('[🎤 MIC] Failed to setup device change listener:', error);
    }
  }

  async startListening() {
    // 开启音频监听
    const shouldSetupAudio = this._config.onAudioData || this._config.onAudioLevel;
      
    if (shouldSetupAudio) {
      console.log(`[🎤 MIC] 🎵 [MicrophoneService #${this.instanceId}] 开始设置音频处理...`);
      await this.setupAudioProcessing();
    } else {
      console.warn(`[🎤 MIC] ⚠️ [MicrophoneService #${this.instanceId}] 跳过音频处理设置 - 没有回调函数`, {
        reasonDetail: {
          hasOnAudioData: !!this._config.onAudioData,
          hasOnAudioLevel: !!this._config.onAudioLevel,
          onAudioDataType: typeof this._config.onAudioData,
          onAudioLevelType: typeof this._config.onAudioLevel
        }
      });
    }
  }

  // Clean up processor node (AudioWorkletNode or ScriptProcessorNode)
  private async endListening() {
    if (this.processorNode) {
      // Handle AudioWorkletNode cleanup
      if ('port' in this.processorNode) {
        // Clear the message handler first
        this.processorNode.port.onmessage = null;
        this.processorNode.port.close();
      } else {
        // Handle ScriptProcessorNode cleanup
        (this.processorNode as any).onaudioprocess = null;
      }
      
      this.processorNode.disconnect();
      this.processorNode = null;
    }
    
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    
    if (this.audioContext) {
      // Set state to suspended first to stop processing
      if (this.audioContext.state !== 'closed') {
        await this.audioContext.suspend();
        await this.audioContext.close();
      }
      this.audioContext = null;
    }
    
    // Reset audio level to 0 when stopping audio processing
    if (this._config.onAudioLevel) {
      this._config.onAudioLevel(0);
    }
  }

  /**
   * Create inline AudioWorklet processor
   */
  private createInlineAudioProcessor(): string {
    return `
      class AudioLevelProcessor extends AudioWorkletProcessor {
        constructor() {
          super();
        }

        process(inputs, outputs, parameters) {
          const input = inputs[0];
          
          // 确保有音频输入
          if (input.length > 0) {
            const inputChannel = input[0]; // 获取第一个声道
            
            if (inputChannel) {
              // 计算音频电平
              let sum = 0;
              for (let i = 0; i < inputChannel.length; i++) {
                sum += Math.abs(inputChannel[i]);
              }
              const level = sum / inputChannel.length;
              
              // 发送音频电平数据到主线程
              this.port.postMessage({
                type: 'audioLevel',
                level: level
              });
              
              // 发送原始音频数据到主线程
              this.port.postMessage({
                type: 'audioData',
                data: inputChannel
              });
            }
          }
          
          // 返回 true 继续处理
          return true;
        }
      }

      // 注册处理器
      registerProcessor('audio-level-processor', AudioLevelProcessor);
    `;
  }

  /**
   * Set up audio processing for real-time audio data using modern AudioWorklet
   */
  private async setupAudioProcessing(): Promise<void> {

    if (!this.mediaStream || (!this._config.onAudioData && !this._config.onAudioLevel)) {
      return;
    }

    try {
      this.audioContext = new AudioContext();
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
      
      // Check if AudioWorklet is supported
      if (!this.audioContext.audioWorklet) {
        console.warn('[🎤 MIC] AudioWorklet not supported, falling back to ScriptProcessorNode');
        this.setupAudioProcessingFallback();
        return;
      }

      // Try inline AudioWorklet processor first (most reliable)
      try {
        console.log('[🎤 MIC] Trying inline AudioWorklet processor...');
        const processorCode = this.createInlineAudioProcessor();
        const blob = new Blob([processorCode], { type: 'application/javascript' });
        const processorUrl = URL.createObjectURL(blob);
        
        await this.audioContext.audioWorklet.addModule(processorUrl);
        console.log('[🎤 MIC] ✅ Inline AudioWorklet processor loaded successfully');
        
        // Clean up the blob URL
        URL.revokeObjectURL(processorUrl);
        
      } catch (inlineError) {
        console.warn('[🎤 MIC] Inline AudioWorklet failed, trying external files...', inlineError);
        
        // Try different paths for the AudioWorklet module as fallback
        const possiblePaths = [
          '/audio-processor.js',
          './audio-processor.js',
          `${window.location.origin}/audio-processor.js`,
          new URL('/audio-processor.js', window.location.origin).href
        ];

        let moduleLoaded = false;
        let lastError: Error | null = null;

        for (const path of possiblePaths) {
          try {
            console.log(`[🎤 MIC] Trying to load AudioWorklet module from: ${path}`);
            await this.audioContext.audioWorklet.addModule(path);
            console.log(`[🎤 MIC] ✅ AudioWorklet module loaded successfully from: ${path}`);
            moduleLoaded = true;
            break;
          } catch (error) {
            console.warn(`[🎤 MIC] ❌ Failed to load AudioWorklet module from ${path}:`, error);
            lastError = error as Error;
            continue;
          }
        }

        if (!moduleLoaded) {
          console.error('[🎤 MIC] All AudioWorklet module paths failed, falling back to ScriptProcessorNode');
          console.error('[🎤 MIC] Last error:', lastError);
          this.setupAudioProcessingFallback();
          return;
        }
      }

      // Create AudioWorklet node
      this.processorNode = new AudioWorkletNode(this.audioContext, 'audio-level-processor');

      // Listen for messages from the processor
      this.processorNode.port.onmessage = (event) => {
        // Check if the service is still initialized before processing
        if (!this.isInitialized) {
          return;
        }
        
        const { type, level, data } = event.data;
        
        if (type === 'audioLevel' && this._config.onAudioLevel) {
          // Scale to 0-100 range
          const scaledLevel = level * 100;
          this._config.onAudioLevel(scaledLevel);
        }
        
        if (type === 'audioData' && this._config.onAudioData) {
          this._config.onAudioData(data);
        }
      };
      
      // Connect the nodes
      this.sourceNode.connect(this.processorNode);
      // Note: AudioWorklet doesn't need to connect to destination for processing
      
      console.log(`[🎤 MIC] ✅ AudioWorklet setup completed successfully`);
      
    } catch (error) {
      console.error('[🎤 MIC] Error in setupAudioProcessing, falling back to ScriptProcessorNode:', error);
      // Fallback to ScriptProcessorNode
      this.setupAudioProcessingFallback();
    }
  }

  /**
   * Fallback audio processing using deprecated ScriptProcessorNode
   * Only used when AudioWorklet is not available
   */
  private setupAudioProcessingFallback(): void {
    if (!this.audioContext || !this.sourceNode) return;
    
    
    // Create processor node for audio processing
    // Note: ScriptProcessorNode is deprecated but still widely supported
    const scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);
    
    scriptProcessor.onaudioprocess = (audioProcessingEvent) => {
      // Check if the service is still initialized before processing
      if (!this.isInitialized) {
        return;
      }
      
      const inputBuffer = audioProcessingEvent.inputBuffer;
      const inputData = inputBuffer.getChannelData(0);
      
      // Calculate audio level for visualization
      const level = inputData.reduce((sum, sample) => sum + Math.abs(sample), 0) / inputData.length;
      const scaledLevel = level * 100;
      
      // Call the audio level callback if provided
      if (this._config.onAudioLevel) {
        this._config.onAudioLevel(scaledLevel); // Scale to 0-100 range
      }
      
      // Call the audio data callback if provided
      if (this._config.onAudioData) {
        this._config.onAudioData(inputData);
      }
    };
    
    // Connect the nodes - 重要：不要连接到destination，这会导致持续处理
    this.sourceNode.connect(scriptProcessor);
    const dummyGain = this.audioContext.createGain();
    dummyGain.gain.value = 0; // 静音，不产生实际输出
    scriptProcessor.connect(dummyGain);
    
    
    // Store reference for cleanup (type assertion for compatibility)
    this.processorNode = scriptProcessor as any;
  }

  /**
   * Destroy the microphone service instance
   */
  async destroy(): Promise<void> {

    // Close microphone first
    await this.closeMicrophone();
    
    this.availableDevices = [];
    this.selectedDeviceId = '';
    
    // Remove device change listener safely
    if (navigator.mediaDevices && 'removeEventListener' in navigator.mediaDevices) {
      try {
        // Note: We need to store the bound function to properly remove it
        // For now, we'll just skip removal as it's not critical for cleanup
        console.log('[🎤 MIC] Skipping device change listener removal (function reference not stored)');
      } catch (error) {
        console.warn('[🎤 MIC] Failed to remove device change listener:', error);
      }
    }
    
    // Update instance count
    MicrophoneService.instanceCount--;
    console.log(`[🎤 MIC] 💀 [MicrophoneService #${this.instanceId}] destroy() - Instance destroyed. Remaining instances: ${MicrophoneService.instanceCount}`);
  }
} 